import React, { Fragment, useEffect, useRef, useState } from "react";

// Icons
import binIcon from "../../../../../assets/icons/bin.svg";
import excelDownload from "../../../../../assets/icons/excelDownload.svg";
import yellowMailingIcon from "../../../../../assets/icons/mailing_list_icon/yellow-sms-tracking.svg";

// Custom Components and Hooks
import { BootstrapTooltip } from "../../../Tooltips/BootstrapTooltip";
import {
  GET_DOCUMENT_REQUEST_MAILING_LIST_USERS,
  createExcelFile,
  extractUserInfo,
  processArray,
} from "../../../Drawers/MailingListDrawer";
import RequestDropdown, {
  CLEAR_SELECTION_TEXT,
} from "../../../Dropdown/RequestDropdown";
import ExcelUploadPopup from "../../ExcelUploadPopup/ExcelUploadPopup";
import {
  ADD_ENTIRE_USERS,
  ADD_ENTIRE_USERS_FROM_EXCEL,
  ADD_USER_TO_MAILING_LIST,
  REMOVE_USER_FROM_MAILING_LIST,
  UPDATE_CURRENT_SELECT_MAILING_LIST,
  UPDATE_USERS_FROM_MAILING_LIST,
} from "../hooks/useRequestMutationForm";

// Apollo Client
import { useLazyQuery, useQuery } from "@apollo/client";

// GraphQL Queries
import {
  GET_ALL_MAILING_LIST,
  formatMailingListResponse,
} from "../../../LeftSection/LeftSection";

// Utilities
import {
  deepCopy,
  isEmailValid,
  optionalChaining,
} from "../../../../../helper";
import { sort } from "fast-sort";

function MailingListSection({ onChange, state = {}, isEdit = false }) {
  const { data: rawMailingListResponse } = useQuery(GET_ALL_MAILING_LIST, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const [
    getUsers,
    { loading: isUserTableDataGettingLoaded, data: userTableData },
  ] = useLazyQuery(GET_DOCUMENT_REQUEST_MAILING_LIST_USERS, {
    fetchPolicy: "network-only",
  });
  const formattedMailingLists = formatMailingListResponse(
    rawMailingListResponse?.documentrequestmailinglist ?? []
  );
  // handlers
  const handleMailingListChange = (selectedValue) => {
    if (selectedValue === CLEAR_SELECTION_TEXT) {
      onChange(UPDATE_CURRENT_SELECT_MAILING_LIST, {
        id: "",
        title: "",
      });
      onChange(ADD_ENTIRE_USERS, []);
      onChange(UPDATE_USERS_FROM_MAILING_LIST, deepCopy([]));
      return;
    }
    const selectedMailingList = formattedMailingLists.find(
      (mailingList) => mailingList.mailingListTitle === selectedValue
    );
    if (selectedMailingList) {
      const { mailingListId, mailingListTitle } = selectedMailingList;
      onChange(UPDATE_CURRENT_SELECT_MAILING_LIST, {
        id: mailingListId,
        title: mailingListTitle,
      });
      getUsers({
        variables: { mailingListId },
      });
    }
  };
  useEffect(() => {
    if (userTableData) {
      const formattedUsers = extractUserInfo(
        userTableData.documentrequestmailinglistusers
      );
      onChange(ADD_ENTIRE_USERS, formattedUsers);
      onChange(UPDATE_USERS_FROM_MAILING_LIST, deepCopy(formattedUsers));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTableData]);
  return (
    <section className="request-mutation-modal-body-third-section">
      <div>
        <div className="my-5">
          <label>
            <span>Mailing List</span>
            {Boolean(state?.isMailingListModified) && (
              <span className="font-italic text-color-020617">
                {"( Modified )"}
              </span>
            )}
          </label>
          <RequestDropdown
            placeholder="Choose Mailing List"
            value={state.currentSelectMailingList.title}
            options={[
              ...sort(formattedMailingLists)
                .asc((mailingList) =>
                  mailingList?.mailingListTitle?.toLowerCase()
                )
                .map((mailingList) => mailingList.mailingListTitle),
              CLEAR_SELECTION_TEXT,
            ]}
            disabled={isEdit}
            onChange={handleMailingListChange}
          />
        </div>

        <div className="mailing-list-header">
          <img
            src={yellowMailingIcon}
            width={"40px"}
            height={"40px"}
            alt="mailing"
          />
          <div>Recipients List </div>
        </div>
        <div className="mailing-list-subheader mb-5">
          {"Use this area to add additional recipients."}
        </div>
      </div>
      <div className="mb-5">
        <UserForm onChange={onChange} isEdit={isEdit} />
        <UsersTable
          usersData={state.users}
          onChange={onChange}
          isLoading={isUserTableDataGettingLoaded}
        />
      </div>
    </section>
  );
}

export default MailingListSection;

// Helper components

function UserForm({ onChange, isEdit = false }) {
  const emailRef = useRef(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isExcelPopupOpened, setExcelPopupOpened] = useState(false);
  const onCloseHandlerForExcelUpload = (
    isForUpload = false,
    usersData = []
  ) => {
    if (isForUpload) {
      onChange(ADD_ENTIRE_USERS_FROM_EXCEL, usersData);
    }
    setExcelPopupOpened(false);
  };

  function isValidInput() {
    // Trim whitespaces and check if the length is greater than two
    const isNameValid = name.trim().length > 1;
    // Return true only if both name and email are valid
    return isNameValid && isEmailValid(email);
  }

  return (
    <div className="recipients-list-user-form-container">
      <div className="recipients-list-user-card">
        <label
          htmlFor="mailingListUserEmail"
          className="mb-3 mailing-drawer-form-label"
        >
          Email Address
        </label>
        <input
          ref={emailRef}
          type="text"
          placeholder="Enter Email Address"
          maxLength="50"
          id="mailingListUserEmail"
          className="min-height-49"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <label
          htmlFor="mailingListUserName"
          className="mb-3 mt-5 mailing-drawer-form-label"
        >
          Full Name
        </label>
        <input
          type="text"
          placeholder="Enter Full Name"
          maxLength="50"
          id="mailingListUserName"
          className="min-height-49"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="recipients-list-user-form-btns">
        <button
          className="light-primary-button mt-5"
          onClick={() => {
            onChange(ADD_USER_TO_MAILING_LIST, { email, name });
            setName("");
            setEmail("");
            if (optionalChaining(emailRef.current, ["focus"], null)) {
              emailRef.current.focus();
            }
          }}
          disabled={!isValidInput()}
        >
          Add User
        </button>
        {/* Need to change for excel */}
        <button
          className="secondary-button mt-5"
          onClick={() => setExcelPopupOpened(true)}
          disabled={isEdit}
          style={{ opacity: isEdit ? 0.5 : 1 }}
        >
          Upload Excel
        </button>
      </div>
      <ExcelUploadPopup
        open={isExcelPopupOpened}
        handleClose={onCloseHandlerForExcelUpload}
      />
    </div>
  );
}

function UsersTable({ usersData = [], onChange, isLoading = false }) {
  const renderCellContent = (value) => {
    if (value.length > 11) {
      return (
        <BootstrapTooltip title={value} placement="bottom-start">
          <div className="cell-content">{`${value.substring(0, 9)}...`}</div>
        </BootstrapTooltip>
      );
    }
    return <div className="cell-content">{value}</div>;
  };

  const renderUsers = () => {
    return processArray(usersData).map((user) => (
      <div className="table-row" key={user.email}>
        <div className="table-cell">{renderCellContent(user.name)}</div>
        <div className="table-cell">{renderCellContent(user.email)}</div>
        <div className="image-cell">
          <img
            alt="del"
            src={binIcon}
            onClick={() => onChange(REMOVE_USER_FROM_MAILING_LIST, user.email)}
          />
        </div>
      </div>
    ));
  };

  const shouldDisplayMessage = usersData.length > 15;

  return (
    <Fragment>
      <div className="users-table">
        <div className="table-header">
          <div className="table-cell">Name</div>
          <div className="table-cell">Email Address</div>
          <div className="image-cell">
            <img
              alt="del"
              src={excelDownload}
              onClick={() => {
                createExcelFile(usersData);
              }}
            />
          </div>
        </div>
        {!isLoading && <div className="table-row-wrapper">{renderUsers()}</div>}
        {isLoading && (
          <div className="mt-4 d-flex justify-content-center">
            {"User data is loading..."}
          </div>
        )}
      </div>
      {shouldDisplayMessage && (
        <div className="mt-2">
          {"Showing first 10 records and last 5 records."}
        </div>
      )}
    </Fragment>
  );
}
